import { getStoredSuperAdminAuth } from "./super_admin_auth_storage";

/** Custom event to refresh the Help menu support badge. */
export const SUPPORT_SUMMARY_REFRESH_EVENT = "adatis:support-summary-refresh";

export const dispatchSupportSummaryRefresh = (): void => {
  window.dispatchEvent(new CustomEvent(SUPPORT_SUMMARY_REFRESH_EVENT));
};

const seenKey = (ticketId: number): string => `adatis.support.seen.${ticketId}`;
const draftKey = (ticketId: number): string => `adatis.support.draft.${ticketId}`;

export const markSupportTicketSeen = (ticketId: number): void => {
  try {
    localStorage.setItem(seenKey(ticketId), new Date().toISOString());
  } catch {
    // ignore quota / private mode
  }
};

export const hasUnseenAgentMessage = (
  ticketId: number,
  lastAgentMessageAt: string | null | undefined
): boolean => {
  if (!lastAgentMessageAt) {
    return false;
  }

  try {
    const seen = localStorage.getItem(seenKey(ticketId));
    if (!seen) {
      return true;
    }

    return new Date(lastAgentMessageAt).getTime() > new Date(seen).getTime();
  } catch {
    return false;
  }
};

export const loadSupportReplyDraft = (ticketId: number): string => {
  try {
    return localStorage.getItem(draftKey(ticketId)) || "";
  } catch {
    return "";
  }
};

export const saveSupportReplyDraft = (ticketId: number, body: string): void => {
  try {
    if (body.trim() === "") {
      localStorage.removeItem(draftKey(ticketId));
      return;
    }

    localStorage.setItem(draftKey(ticketId), body);
  } catch {
    // ignore
  }
};

export const clearSupportReplyDraft = (ticketId: number): void => {
  try {
    localStorage.removeItem(draftKey(ticketId));
  } catch {
    // ignore
  }
};

export const hasSupportReplyDraft = (ticketId: number): boolean =>
  loadSupportReplyDraft(ticketId).trim() !== "";

const COMPOSE_DRAFT_PREFIX = "adatis.support.compose.";
const MUTE_BEEP_KEY = "adatis.support.muteBeep";
const POLL_BASE_MS_KEY = "adatis.support.pollBaseMs";

/** Preferred focused poll intervals until Reverb (`GAP-F2-085`). */
export const SUPPORT_POLL_BASE_OPTIONS_MS = [5_000, 12_000, 60_000] as const;

export type SupportPollBaseMs = (typeof SUPPORT_POLL_BASE_OPTIONS_MS)[number];

export type SupportComposeMode = "ticket" | "feedback";

export type SupportComposeDraft = {
  category?: string;
  subject: string;
  description: string;
  impact?: string;
};

const composeDraftKey = (mode: SupportComposeMode): string =>
  `${COMPOSE_DRAFT_PREFIX}${mode}`;

export const loadSupportComposeDraft = (
  mode: SupportComposeMode
): SupportComposeDraft | null => {
  try {
    const raw = localStorage.getItem(composeDraftKey(mode));
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as SupportComposeDraft;
    if (typeof parsed.subject !== "string" || typeof parsed.description !== "string") {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
};

export const saveSupportComposeDraft = (
  mode: SupportComposeMode,
  draft: SupportComposeDraft
): void => {
  try {
    const hasContent =
      draft.subject.trim() !== "" ||
      draft.description.trim() !== "" ||
      Boolean(draft.impact);

    if (!hasContent) {
      localStorage.removeItem(composeDraftKey(mode));
      return;
    }

    localStorage.setItem(composeDraftKey(mode), JSON.stringify(draft));
  } catch {
    // ignore
  }
};

export const clearSupportComposeDraft = (mode: SupportComposeMode): void => {
  try {
    localStorage.removeItem(composeDraftKey(mode));
  } catch {
    // ignore
  }
};

export const hasSupportComposeDraft = (mode: SupportComposeMode): boolean => {
  const draft = loadSupportComposeDraft(mode);

  return Boolean(
    draft &&
      (draft.subject.trim() !== "" ||
        draft.description.trim() !== "" ||
        Boolean(draft.impact))
  );
};

export const isSupportBeepMuted = (): boolean => {
  try {
    return localStorage.getItem(MUTE_BEEP_KEY) === "1";
  } catch {
    return false;
  }
};

export const setSupportBeepMuted = (muted: boolean): void => {
  try {
    if (muted) {
      localStorage.setItem(MUTE_BEEP_KEY, "1");
    } else {
      localStorage.removeItem(MUTE_BEEP_KEY);
    }
  } catch {
    // ignore
  }
};

/** Soft short beep for in-panel new-message cue (no asset required). */
export const playSoftNotificationBeep = (): void => {
  if (isSupportBeepMuted()) {
    return;
  }

  try {
    const AudioCtx =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext;

    if (!AudioCtx) {
      return;
    }

    const context = new AudioCtx();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "sine";
    oscillator.frequency.value = 880;
    gain.gain.value = 0.04;
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + 0.18);
    oscillator.stop(context.currentTime + 0.18);
  } catch {
    // ignore autoplay / unsupported
  }
};

export const notifySupportAgentReplyBrowser = (
  title: string,
  body: string,
  ticketId: number
): void => {
  if (typeof window === "undefined" || typeof Notification === "undefined") {
    return;
  }

  if (document.visibilityState === "visible") {
    return;
  }

  const show = () => {
    try {
      const notification = new Notification(title, {
        body,
        tag: `support-ticket-${ticketId}`,
      });
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch {
      // ignore
    }
  };

  if (Notification.permission === "granted") {
    show();
    return;
  }

  if (Notification.permission === "default") {
    void Notification.requestPermission().then((permission) => {
      if (permission === "granted") {
        show();
      }
    });
  }
};

export const getSupportPollBaseMs = (): SupportPollBaseMs | null => {
  try {
    const raw = localStorage.getItem(POLL_BASE_MS_KEY);
    if (!raw) {
      return null;
    }

    const parsed = Number(raw);
    if (
      SUPPORT_POLL_BASE_OPTIONS_MS.includes(parsed as SupportPollBaseMs)
    ) {
      return parsed as SupportPollBaseMs;
    }
  } catch {
    // ignore
  }

  return null;
};

export const setSupportPollBaseMs = (ms: SupportPollBaseMs | null): void => {
  try {
    if (ms === null) {
      localStorage.removeItem(POLL_BASE_MS_KEY);
      return;
    }

    localStorage.setItem(POLL_BASE_MS_KEY, String(ms));
  } catch {
    // ignore
  }
};

/**
 * Adaptive live-update intervals — WebSocket/Echo deferred until Reverb (`GAP-F2-085`).
 * Optional preferred base (perfil SA) overrides the focused tier.
 */
export const resolveSupportLivePollMs = (): number => {
  const preferred = getSupportPollBaseMs();

  if (document.visibilityState !== "visible") {
    return Math.max(preferred ?? 60_000, 60_000);
  }

  if (typeof document.hasFocus === "function" && document.hasFocus()) {
    return preferred ?? 5_000;
  }

  if (preferred !== null) {
    return Math.max(preferred, 12_000);
  }

  return 12_000;
};

export type SupportRecurringThemeBacklogInput = {
  subject: string;
  category: string;
  categoryLabel: string;
  count: number;
  suggestedId?: string | null;
};

/** Public GitHub repo — used only to draft an issue (never auto-writes `_DOCS/`). */
export const ADATIS_GITHUB_ISSUES_NEW_URL =
  "https://github.com/cecmedeiros/adatis/issues/new";

/** Markdown row for human paste into `_DOCS/28` — never auto-writes docs. */
export const buildRecurringThemeBacklogMarkdown = (
  theme: SupportRecurringThemeBacklogInput
): string => {
  const hub = "suporte-chamados";
  const suggestedId =
    theme.suggestedId && theme.suggestedId.trim() !== ""
      ? theme.suggestedId.trim()
      : "ADJ-F2-???";
  const description = `${theme.subject} — ${theme.count}× em 90d (categoria ${theme.categoryLabel} / \`${theme.category}\`); origem temas recorrentes SA`;

  return [
    "<!-- Colar em `_DOCS/28_lacunas_bugs_e_ajustes.md` § 4 (humano no PR/chat). Proibido gravar docs via API/UI. -->",
    "",
    `| **${suggestedId}** | lacuna | P2 | **F2** | M3 | ux · ${description} | [modulos/${hub}](modulos/${hub}.md) |`,
    "",
    `- **ID sugerido:** \`${suggestedId}\`${
      suggestedId === "ADJ-F2-???"
        ? " (substituir pelo próximo livre em 28)"
        : " (confirmar se ainda está livre em 28 § 4)"
    }`,
    `- **Tipo:** lacuna`,
    `- **P:** P2`,
    `- **Módulo:** M3 / ${hub}`,
    `- **Descrição:** ${theme.subject} (${theme.count}×, ${theme.categoryLabel})`,
    `- **Hub:** \`modulos/${hub}.md\``,
  ].join("\n");
};

export const buildRecurringThemeGithubIssueUrl = (
  theme: SupportRecurringThemeBacklogInput
): string => {
  const markdown = buildRecurringThemeBacklogMarkdown(theme);
  const suggestedId =
    theme.suggestedId && theme.suggestedId.trim() !== ""
      ? theme.suggestedId.trim()
      : "ADJ-F2-???";
  const params = new URLSearchParams({
    title: `[backlog] ${suggestedId} — ${theme.subject}`,
    body: [
      "Rascunho a partir de tema recorrente (Super Admin). Colar também em `_DOCS/28` § 4 se aprovado.",
      "",
      "```markdown",
      markdown,
      "```",
    ].join("\n"),
  });

  return `${ADATIS_GITHUB_ISSUES_NEW_URL}?${params.toString()}`;
};

/**
 * Human relative remaining time until `iso` (e.g. "em 2h").
 * Returns null when expired or invalid.
 */
export const formatRelativeUntil = (
  iso: string | null | undefined,
  locale: string = "pt-BR"
): string | null => {
  if (!iso) {
    return null;
  }

  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) {
    return null;
  }

  const diffMs = target - Date.now();
  if (diffMs <= 0) {
    return null;
  }

  const minutes = Math.round(diffMs / 60_000);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  if (minutes < 60) {
    return rtf.format(Math.max(1, minutes), "minute");
  }

  const hours = Math.round(minutes / 60);
  if (hours < 48) {
    return rtf.format(hours, "hour");
  }

  return rtf.format(Math.round(hours / 24), "day");
};

export type SupportSlaTone = "ok" | "warn" | "critical";

export const resolveAgentSla = (
  lastAgentMessageAt: string | null | undefined
): { hours: number; tone: SupportSlaTone } | null => {
  if (!lastAgentMessageAt) {
    return null;
  }

  const hours =
    (Date.now() - new Date(lastAgentMessageAt).getTime()) / (1000 * 60 * 60);

  if (Number.isNaN(hours) || hours < 0) {
    return null;
  }

  if (hours >= 24) {
    return { hours, tone: "critical" };
  }

  if (hours >= 4) {
    return { hours, tone: "warn" };
  }

  return { hours, tone: "ok" };
};

export const formatSlaLabel = (hours: number): string => {
  if (hours < 1) {
    const minutes = Math.max(1, Math.round(hours * 60));

    return `${minutes}m`;
  }

  if (hours < 24) {
    return `${Math.round(hours)}h`;
  }

  return `${Math.round(hours / 24)}d`;
};

export type SupportTicketLinkScope = "tenant" | "admin";

export const buildSupportTicketPath = (
  scope: SupportTicketLinkScope,
  ticketId: number
): string =>
  scope === "admin"
    ? `/super-admin/support/tickets/${ticketId}`
    : `/support/tickets/${ticketId}`;

export const buildSupportTicketAbsoluteUrl = (
  scope: SupportTicketLinkScope,
  ticketId: number
): string => `${window.location.origin}${buildSupportTicketPath(scope, ticketId)}`;

export const copySupportTicketLink = async (
  scope: SupportTicketLinkScope,
  ticketId: number
): Promise<boolean> => {
  const url = buildSupportTicketAbsoluteUrl(scope, ticketId);

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      return true;
    }
  } catch {
    // fallback below
  }

  try {
    const input = document.createElement("input");
    input.value = url;
    input.setAttribute("readonly", "");
    input.style.position = "absolute";
    input.style.left = "-9999px";
    document.body.appendChild(input);
    input.select();
    const ok = document.execCommand("copy");
    input.remove();
    return ok;
  } catch {
    return false;
  }
};

/** Built-in SA reply snippets — bodies come from i18n (`admin.supportTickets.snippets.*`). */
export const SUPPORT_QUICK_REPLY_KEYS = [
  "askMoreInfo",
  "checkResolved",
  "waitingLogs",
  "thanksClosing",
] as const;

export type SupportQuickReplyKey = (typeof SUPPORT_QUICK_REPLY_KEYS)[number];

export const resolveCurrentSuperAdminId = (): number | null => {
  const auth = getStoredSuperAdminAuth();
  const user = auth?.user;
  const rawId = user?.id ?? user?.sub;

  if (typeof rawId === "number" && Number.isFinite(rawId)) {
    return rawId;
  }

  if (typeof rawId === "string" && rawId.trim() !== "") {
    const parsed = Number(rawId);

    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const SNIPPETS_KEY = "adatis.support.customSnippets";

export const loadCustomSupportSnippets = (): Partial<
  Record<SupportQuickReplyKey, string>
> => {
  try {
    const raw = localStorage.getItem(SNIPPETS_KEY);

    if (!raw) {
      return {};
    }

    return JSON.parse(raw) as Partial<Record<SupportQuickReplyKey, string>>;
  } catch {
    return {};
  }
};

export const saveCustomSupportSnippet = (
  key: SupportQuickReplyKey,
  body: string
): void => {
  try {
    const current = loadCustomSupportSnippets();

    if (body.trim() === "") {
      delete current[key];
    } else {
      current[key] = body;
    }

    if (Object.keys(current).length === 0) {
      localStorage.removeItem(SNIPPETS_KEY);
      return;
    }

    localStorage.setItem(SNIPPETS_KEY, JSON.stringify(current));
  } catch {
    // ignore quota / private mode
  }
};

export const clearCustomSupportSnippet = (key: SupportQuickReplyKey): void => {
  try {
    const current = loadCustomSupportSnippets();
    delete current[key];

    if (Object.keys(current).length === 0) {
      localStorage.removeItem(SNIPPETS_KEY);
      return;
    }

    localStorage.setItem(SNIPPETS_KEY, JSON.stringify(current));
  } catch {
    // ignore
  }
};

export const resolveSupportSnippetBody = (
  key: SupportQuickReplyKey,
  fallback: string
): string => {
  const custom = loadCustomSupportSnippets()[key];

  return custom && custom.trim() !== "" ? custom : fallback;
};

export type SupportSnoozeItem = {
  ticketId: number;
  publicNumber: string;
  subject: string;
  remindAt: string;
};

const SNOOZE_KEY = "adatis.support.snoozes";

export const loadSupportSnoozes = (): SupportSnoozeItem[] => {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);

    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as SupportSnoozeItem[];

    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const persistSupportSnoozes = (items: SupportSnoozeItem[]): void => {
  try {
    if (items.length === 0) {
      localStorage.removeItem(SNOOZE_KEY);
      return;
    }

    localStorage.setItem(SNOOZE_KEY, JSON.stringify(items));
  } catch {
    // ignore
  }
};

export const upsertSupportSnooze = (item: SupportSnoozeItem): void => {
  const current = loadSupportSnoozes().filter(
    (entry) => entry.ticketId !== item.ticketId
  );
  current.push(item);
  persistSupportSnoozes(current);
};

export const removeSupportSnooze = (ticketId: number): void => {
  persistSupportSnoozes(
    loadSupportSnoozes().filter((entry) => entry.ticketId !== ticketId)
  );
};

export const getSupportSnooze = (ticketId: number): SupportSnoozeItem | null =>
  loadSupportSnoozes().find((entry) => entry.ticketId === ticketId) ?? null;

export const consumeDueSupportSnoozes = (): SupportSnoozeItem[] => {
  const now = Date.now();
  const all = loadSupportSnoozes();
  const due = all.filter(
    (entry) => new Date(entry.remindAt).getTime() <= now
  );
  const remaining = all.filter(
    (entry) => new Date(entry.remindAt).getTime() > now
  );

  if (due.length > 0) {
    persistSupportSnoozes(remaining);
  }

  return due;
};

const SUPPORT_LIVE_CHANNEL = "adatis.support.live";

export type SupportLiveUpdatePayload = {
  ticketId: number;
  lastAgentMessageAt: string;
};

export const publishSupportLiveUpdate = (
  payload: SupportLiveUpdatePayload
): void => {
  try {
    const channel = new BroadcastChannel(SUPPORT_LIVE_CHANNEL);
    channel.postMessage({ type: "agent_reply", ...payload });
    channel.close();
  } catch {
    // ignore unsupported browsers
  }
};

export const subscribeSupportLiveUpdates = (
  onUpdate: (payload: SupportLiveUpdatePayload) => void
): (() => void) => {
  try {
    const channel = new BroadcastChannel(SUPPORT_LIVE_CHANNEL);
    channel.onmessage = (event: MessageEvent) => {
      const data = event.data as {
        type?: string;
        ticketId?: number;
        lastAgentMessageAt?: string;
      };

      if (
        data?.type === "agent_reply" &&
        typeof data.ticketId === "number" &&
        typeof data.lastAgentMessageAt === "string"
      ) {
        onUpdate({
          ticketId: data.ticketId,
          lastAgentMessageAt: data.lastAgentMessageAt,
        });
      }
    };

    return () => channel.close();
  } catch {
    return () => undefined;
  }
};
